<template>
  <form @submit.prevent="submit">
    <!-- Hidden username so password managers associate the new credentials correctly. -->
    <input type="text"
      autocomplete="username"
      class="is-hidden"
      :value="username">

    <p class="mb-4">First time signing in! Please enter and confirm a new password</p>

    <div class="control mb-4">
      <div class="field">
        <label class="label"
          :for="`${idPrefix}-newpassword`">New Password</label>
        <input :id="`${idPrefix}-newpassword`"
          class="input"
          type="password"
          placeholder="New Password"
          autocomplete="new-password"
          :disabled="isSubmitting"
          v-model="newPassword"
          @input="onNewPasswordInput">
        <span v-if="touched && !newPassword"
          class="help is-danger ml-1">Required</span>
      </div>

      <div class="field">
        <label class="label"
          :for="`${idPrefix}-confirmpassword`">Confirm Password</label>
        <input :id="`${idPrefix}-confirmpassword`"
          class="input"
          type="password"
          placeholder="Confirm Password"
          autocomplete="new-password"
          :disabled="isSubmitting"
          v-model="confirmPassword">
        <span v-if="touched && !confirmPassword"
          class="help is-danger ml-1">Required</span>
        <span v-else-if="confirmPassword && confirmPassword !== newPassword"
          class="help is-danger ml-1">Passwords do not match</span>
      </div>

      <!-- Legacy #passwordValidityLabel: one message below both fields, green or red. -->
      <span v-if="complexityMessage"
        class="password-validity"
        :class="meetsCriteria ? 'is-strong' : 'is-weak'">{{ complexityMessage }}</span>
    </div>

    <div class="control is-flex is-justify-content-end">
      <button type="submit"
        class="button is-primary"
        :disabled="isSubmitting">
        Save
        <span class="icon ml-1">
          <i class="mdi mdi-18px"
            :class="isSubmitting ? 'mdi-loading mdi-spin' : 'mdi-arrow-right'" />
        </span>
      </button>
    </div>
  </form>
</template>

<script>
import FqApi from '@/services/fq-api'

const COMPLEXITY_DEBOUNCE_MS = 350

/**
 * First-login password change, shared by /login and /login-csr. Both reach it the same way: the
 * server refuses to issue a token for an account still carrying its admin-assigned password and
 * hands back a ticket instead.
 *
 * Emits `submit` with the chosen password once it is confirmed and meets the server's criteria.
 * The parent owns the ticket and the call, since the two flows continue differently afterwards.
 */
export default {
  name: 'NewPasswordPanel',
  props: {
    // Feeds the hidden field password managers read; not submitted.
    username: {
      type: String,
      default: ''
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    // Keeps input ids unique when both panels exist in one page's markup.
    idPrefix: {
      type: String,
      default: 'newpassword'
    }
  },
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      complexityMessage: '',
      meetsCriteria: false,
      touched: false
    }
  },
  beforeDestroy() {
    clearTimeout(this._complexityTimer)
  },
  methods: {
    onNewPasswordInput() {
      // Debounced so this is not one request per keystroke.
      clearTimeout(this._complexityTimer)
      this._complexityTimer = setTimeout(this.checkComplexity, COMPLEXITY_DEBOUNCE_MS)
    },

    async checkComplexity() {
      if (!this.newPassword) {
        this.complexityMessage = ''
        this.meetsCriteria = false
        return
      }
      try {
        // POST, so the plaintext password never appears in a request URI - and therefore not in
        // access logs, browser history or error breadcrumbs.
        const { data } = await FqApi.post('/auth/postPasswordComplexity', { password: this.newPassword })
        this.meetsCriteria = data.passwordMeetsCriteria
        this.complexityMessage = data.passwordMeetsCriteria ? 'Password is strong' : data.message
      } catch (e) {
        this.meetsCriteria = false
        this.complexityMessage = 'Could not validate password'
      }
    },

    async submit() {
      this.touched = true
      if (!this.newPassword || !this.confirmPassword) return
      if (this.newPassword !== this.confirmPassword) return

      // The debounce may still be pending on a fast submit, so settle the check before relying
      // on it rather than posting a password the server will reject.
      clearTimeout(this._complexityTimer)
      await this.checkComplexity()
      if (!this.meetsCriteria) return

      this.$emit('submit', this.newPassword)
    }
  }
}
</script>

<style lang="scss" scoped>
.label {
  font-weight: 500;
}
// login.css .success / .danger - plain green and red at body size, not Bulma help text.
.password-validity {
  &.is-strong {
    color: green;
  }
  &.is-weak {
    color: red;
  }
}
</style>
